import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { Entity } from '../../entity/entity';
import { PartSearchResult } from '../../products/part-search-result';
import { EntityService } from '../../services/entity-service';
import { PartsService } from '../../services/parts-service';
import { QuoteService } from '../../services/quote-service';
import { StringUtils } from '../../utils/string-utils';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { Part } from '../../products/part';
import { FileUtils } from "../../utils/file-utils";
import { DialogService } from "../../services/dialog.service";
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { AsyncPipe, CurrencyPipe, NgClass, NgStyle } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PersonLoginService } from "../../services/person-login-service";
import { SerialNumberResearchService } from "../../services/serial-number-research.service";
import { PartSerialInformation } from "./part-serial-information";
import { WarrantyClaim } from "./warranty-claim";
import { SewpWarranty } from "./sewp-warranty";
import { DecoderData } from "../../model/decoder-data";
import { DecoderTableComponent } from "../../decoder/decoder-table.component";
import { StdWarranty } from "./std-warranty";
import { ExtWarranty } from "./ext-warranty";
import { MatTab, MatTabGroup } from "@angular/material/tabs";
import { DealerConsumerInformation } from "./dealer-consumer-information";
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from "@angular/material/autocomplete";
import { BrandID } from "../../enums/brand-id";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { SelectionItem } from "../../selections/selection-item";
import { OrganizationProjectProductTypeService } from '../../services/organization-projectproducttype-service';
import { PersonService } from '../../services/person-service';
import { PersonLogin } from '../../person/person-login';
import { MatSelectModule } from '@angular/material/select';
import { ProjectProductType } from '../../enums/project-product-type';
import { DefectCode } from "../../model/defect-code";
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from "@angular/material/datepicker";
import { DateUtils } from "../../utils/date-utils";
import { MatInput, MatInputModule } from "@angular/material/input";
import { WarrantyLineService } from "../../services/warranty-line.service";
import { WarrantyLine } from "../../model/warranty-line";
import { EntityLineService } from "../../services/entity-line-service";
import { EntityLine } from "../../entity/entity-line";
import { EntityLineHeader } from "../../entity/entity-line-header";
import { OrganizationAddress } from "../../organization/organization-address";
import { AddressSelectionDialogComponent } from "../address-selection-dialog/address-selection-dialog.component";
import { DealerAddressSelectionDialogComponent } from '../dealer-address-selection-dialog/dealer-address-selection-dialog.component';
import { OrganizationAddressService } from '../../services/organization-address-service';
import { MatTooltipModule } from '@angular/material/tooltip';


@Component({
  selector: 'parts-dialog',
  templateUrl: './parts-dialog.component.html',
  styleUrls: ['./parts-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatSelectModule, FormsModule, MatButton, MatIcon, MatProgressSpinner, MatDialogActions, CurrencyPipe, MatTab, MatTabGroup, MatDialogContent, MatAutocompleteTrigger, MatOption, MatAutocomplete, ReactiveFormsModule, AsyncPipe, NgClass, MatDatepicker, MatDatepickerInput, MatDatepickerToggle, MatTooltipModule, MatInputModule, DecoderTableComponent]
})
export class PartsDialogComponent implements OnInit {
  private readonly dialog = inject(MatDialog);
  private readonly partsService = inject(PartsService);
  private readonly quoteService = inject(QuoteService);
  private readonly entityService = inject(EntityService);
  private readonly dialogRef = inject<MatDialogRef<PartsDialogComponent>>(MatDialogRef);
  private readonly dialogService = inject(DialogService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly serialNumberResearchService = inject(SerialNumberResearchService);
  private readonly organizationProjectProductTypeService = inject(OrganizationProjectProductTypeService);
  private readonly warrantyLineService = inject(WarrantyLineService);
  private readonly entityLineService = inject(EntityLineService);
  private readonly addressService = inject(OrganizationAddressService);
  private readonly data = inject(MAT_DIALOG_DATA);
  private personLogin: PersonLogin;
  entity: Entity;
  readonly warranty = signal<boolean>(false);
  readonly warrantyAvailable = signal<boolean>(true);
  readonly searchResult = signal<PartSearchResult>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly partWithinPartErrorMessage = signal<string>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  warrantyRequestNotes!: string;
  readonly partSerialInformation = signal<PartSerialInformation>(undefined!);
  readonly warrantyClaims = signal<WarrantyClaim[]>(undefined!);
  readonly sewpWarranties = signal<SewpWarranty[]>(undefined!);
  readonly decoderData = signal<DecoderData>(undefined!);
  availableDrawings!: string[];
  readonly stdWarranties = signal<StdWarranty[]>(undefined!);
  readonly extWarranties = signal<ExtWarranty[]>(undefined!);
  readonly dealerConsumerInformation = signal<DealerConsumerInformation>(undefined!);
  readonly filterData = signal<SelectionItem[]>(undefined!);
  readonly filteredOptions = signal<Observable<SelectionItem[]>>(undefined!);
  partSearchTermInputControl = new FormControl();
  readonly userProjectProductTypes = signal<ProjectProductType[]>([]);
  readonly waitMessage = signal<string>('Please wait...');
  allPartsSearchInputControl = new FormControl();
  determinedBrand: BrandID = BrandID.CLM;
  readonly determinedPPT = signal<ProjectProductType>(undefined!);
  readonly defectCodes = signal<DefectCode[]>([]);
  miles: SelectionItem[] = this.createMilesSelectionItems();
  tabIndex = 0;
  readonly warrantyLines = signal<WarrantyLine[]>([]);
  readonly entityLineHeaders = signal<EntityLineHeader[]>([]);
  addresses!: OrganizationAddress[];
  selectedAddress!: OrganizationAddress;
  today: Date = new Date();
  readonly dealerAddress = signal<OrganizationAddress>(undefined!);
  maxWarrantyTextLength = 120;

  constructor() {
    this.entity = this.data.entity as Entity;
    this.personLogin = this.personLoginService.getCachedLogin()!;
    if (this.data.entity) {
      this.loadWarrantyLines();
      this.loadEntityLineHeaders();
      this.getDealerAddress();
    }
    if (!this.isAddQuote()) {
      this.determinedBrand = this.entity.brandID;
      this.determinedPPT.set(this.entity.projectProductType);
    }
  }

  ngOnInit(): void {
    if (this.isAddQuote()) {
      this.isBusy.set(true);
      this.organizationProjectProductTypeService.getByOrgUnitId(this.personLogin.person.primaryOrgUnitId).subscribe({
        next: results => {
          this.userProjectProductTypes.set((results).map((oxppt: any) => oxppt.projectProductType));
          if (this.userProjectProductTypes().length === 1) {
            this.determinedPPT.set(this.userProjectProductTypes()[0]);
          } else {
            this.partSearchTermInputControl.disable();
          }
        },
        error: error => this.handleError(error),
        complete: () => {
          this.isBusy.set(false);
        }
      });
    }
  }

  createMilesSelectionItems(): SelectionItem[] {
    const selectionItems: SelectionItem[] = [];
    let selectionItem = new SelectionItem();
    selectionItem.code = '0-25';
    selectionItem.description = '0-25'
    selectionItems.push(selectionItem);
    selectionItem = new SelectionItem();
    selectionItem.code = '25-50';
    selectionItem.description = '25-50'
    selectionItems.push(selectionItem);
    selectionItem = new SelectionItem();
    selectionItem.code = '50+';
    selectionItem.description = '50+'
    selectionItems.push(selectionItem);
    return selectionItems;
  }

  determinedPPTChanged(): void {
    this.partSearchTermInputControl.enable();
  }

  displayFn(selectionItem: SelectionItem): string {
    return selectionItem?.description || '';
  }

  private inputControlCodeValue(value: any): string {
    return typeof value === "object" ? value.code : value;
  }

  private _filter(value: any): SelectionItem[] {
    value = this.inputControlCodeValue(value);
    const filterValue = value.toLowerCase();
    return this.filterData().filter((selectionItem: SelectionItem) => selectionItem.description.toLowerCase().includes(filterValue));
  }

  public saveAndClose(): void {
    this.isBusy.set(true);
    this.searchResult().warrantyRequestNotes = this.warrantyRequestNotes;
    if (this.warranty()) {
      this.searchResult().dealerAddressId = this.dealerAddress().addressId;
    }
    if (this.isAddQuote()) {
      this.quoteService.createPartsQuote(this.searchResult(), this.determinedBrand, this.determinedPPT()).subscribe({
        next: result => {
          this.isBusy.set(false);
          this.dialogRef.close(result);
        }, error: error => this.handleError(error)
      });
    } else {
      this.entityService.addParts(this.entity.id.entityId, this.entity.id.entityRevision, this.searchResult()).subscribe({
        next: result => {
          this.isBusy.set(false);
          this.dialogRef.close(result);
        }, error: error => this.handleError(error)
      });
    }
  }

  public handleAllPartsKeyUp(keyEvent: KeyboardEvent): void {
    if (keyEvent.keyCode === 13) {
      this.allPartsSearch();
    }
    if (this.allPartsSearchInputControl && this.allPartsSearchInputControl.value && this.allPartsSearchInputControl.value.length <= 4) {
      this.filterData.set([]);
    }
    if (this.allPartsSearchInputControl && this.allPartsSearchInputControl.value && this.allPartsSearchInputControl.value.length == 5) {
      this.partsService.getPartsFilterData(this.allPartsSearchInputControl.value as string, this.determinedBrand).subscribe({
        next: (data: SelectionItem[]) => {
          this.filterData.set(data);
          this.filteredOptions.set(this.allPartsSearchInputControl.valueChanges.pipe(
            startWith(''),
            //map(value => this._filter(value || '')),
            map((value: any): SelectionItem[] => {
              return this._filter(value) || [];
            }),
          ));
        },
        error: (error) => {
          this.handleError(error);
        }
      });
    }
  }

  public handleKeyUp(keyEvent: KeyboardEvent): void {
    if (keyEvent.key === 'Enter') {
      this.search();
    }
    if (this.partSearchTermInputControl?.value?.length <= 4) {
      this.filterData.set([]);
    }

    if (this.partSearchTermInputControl?.value?.length == 5) {
      this.partsService.getPartsFilterData(this.partSearchTermInputControl.value as string, this.determinedBrand).subscribe({
        next: (data: SelectionItem[]) => {
          this.filterData.set(data);
          this.filteredOptions.set(this.partSearchTermInputControl.valueChanges.pipe(
            startWith(''),
            map((value: any): SelectionItem[] => {
              return this._filter(value) || [];
            }),
          ));
        },
        error: (error) => {
          this.handleError(error);
        }
      });
    }
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  public warrantyChanged(): void {
    this.searchResult.set(null!);
  }

  public determineStdWarrantyDescription(stdWarranty: StdWarranty): string {
    return (this.partSerialInformation()?.rescom === 'C' && stdWarranty?.warrantyTitle === 'STD LABOR')
      ? 'NA' : stdWarranty?.warrantyDesc;
  }

  private doPartNumberLookups(entityId: string, entityRevision: number): void {
    this.waitMessage.set('Searching for parts...<br>This may take several seconds.');
    let searchInputControlCodeValue: string = this.inputControlCodeValue(this.partSearchTermInputControl.value);
    this.partsService.searchParts(searchInputControlCodeValue, this.warranty(), entityId, entityRevision,
      this.determinedBrand, this.determinedPPT(), null!).subscribe({
        next: result => {
          if (result && result.parts && result.parts.length > 0) {
            this.searchResult.set(result);
            this.serialNumberResearchService.getDecoderData(searchInputControlCodeValue).subscribe(result => {
              this.decoderData.set(result);
            }, error => this.handleError(error));
          } else {
            this.serialNumberResearchService.getReplacementParts(this.inputControlCodeValue(this.partSearchTermInputControl.value)).subscribe({
              next: (parts: Part[]) => {
                let psr = new PartSearchResult();
                psr.parts = parts;
                psr.searchTerm = this.inputControlCodeValue(this.partSearchTermInputControl.value);
                this.searchResult.set(psr);
              },
              error: error => this.handleError(error)
            });
          }
        }, error: error => this.handleError(error),
        complete: () => {
          this.waitMessage.set('Please wait...');
          this.isBusy.set(false);
        }
      });
  }

  private doSerialNumberLookups(entityId: string, entityRevision: number): void {
    this.waitMessage.set('Searching serial number...<br>This may take several seconds.');
    let searchInputControlCodeValue: string = this.inputControlCodeValue(this.partSearchTermInputControl.value);
    this.partsService.searchParts(searchInputControlCodeValue, this.warranty(), entityId, entityRevision,
      this.determinedBrand, this.determinedPPT(), this.partSerialInformation().built_date).subscribe({
        next: result => {
          if (!result) {
            this.errorMessage.set('There was an error searching for parts');
          } else {
            if (this.warranty() && !result.warranty) {
              this.errorMessage.set("Warranty searches are not valid for this " + this.entity.id.entityName.toLowerCase());
              this.warrantyAvailable.set(false);
            }
            this.serialNumberResearchService.getDefectCodesOrDefault(searchInputControlCodeValue).subscribe({
              next: result => this.defectCodes.set(result),
              error: error => this.handleError(error),
            });
            this.serialNumberResearchService.getDecoderData(this.partSerialInformation().prdno).subscribe({
              next: result => this.decoderData.set(result),
              error: error => this.handleError(error),
            });
            this.serialNumberResearchService.getStdWarranties(this.partSerialInformation()).subscribe({
              next: result => this.stdWarranties.set(result),
              error: error => this.handleError(error)
            });
            this.serialNumberResearchService.getExtWarranties(this.partSerialInformation()).subscribe({
              next: result => this.extWarranties.set(result),
              error: error => this.handleError(error)
            });
            this.serialNumberResearchService.getDealerConsumerInformation(searchInputControlCodeValue).subscribe({
              next: result => this.dealerConsumerInformation.set(result),
              error: error => this.handleError(error)
            });
            this.serialNumberResearchService.getWarrantyClaims(searchInputControlCodeValue).subscribe({
              next: result => this.warrantyClaims.set(result || []),
              error: error => this.handleError(error)
            });
            this.serialNumberResearchService.getSewpWarranties(searchInputControlCodeValue).subscribe({
              next: result => this.sewpWarranties.set(result || []),
              error: error => this.handleError(error)
            });
            this.warranty.set(result.warranty);
            this.searchResult.set(result);

            if (this.partSerialInformation().install_date && this.partSerialInformation().install_date.length > 0) {
              this.searchResult().installDate = DateUtils.parseDate(this.partSerialInformation().install_date + " CST");
            }
          }
        }, error: error => this.handleError(error),
        complete: () => {
          this.waitMessage.set('Please wait...');
          this.isBusy.set(false);
        }

      });
  }

  public allPartsSearch(): void {
    this.isBusy.set(true);
    this.partWithinPartErrorMessage.set(null!);
    let entityId: string = this.isAddQuote() ? '' : this.entity.id.entityId;
    let entityRevision: number = this.isAddQuote() ? 0 : this.entity.id.entityRevision;

    this.partsService.searchParts(this.inputControlCodeValue(this.allPartsSearchInputControl.value), false, entityId, entityRevision, this.determinedBrand, this.determinedPPT(), null!).subscribe(result => {
      if (result && result.parts && result.parts.length > 0) {
        let partWithinPart = result.parts[0];
        partWithinPart.partWithinPartSearch = true;
        this.searchResult().parts.unshift(partWithinPart);
        this.scrollPartsTableToTop();
      } else {
        this.serialNumberResearchService.getReplacementParts(this.inputControlCodeValue(this.allPartsSearchInputControl.value)).subscribe({
          next: (parts: Part[]) => {
            if (parts && parts.length > 0) {
              let partWithinPart = parts[0];
              partWithinPart.partWithinPartSearch = true;
              this.searchResult().parts.unshift(partWithinPart);
              this.scrollPartsTableToTop();
            } else {
              this.partWithinPartErrorMessage.set("Unable to find part: " + this.inputControlCodeValue(this.allPartsSearchInputControl.value));
            }
          },
          error: error => this.handleError(error)
        });
      }

      this.isBusy.set(false);
    }, error => this.handleError(error));
  }

  public scrollPartsTableToTop(): void {
    document.getElementById('partTable')!.scrollIntoView();
  }

  public search(): void {
    if (this.warrantyLines() && this.warrantyLines().length > 0 && !this.warranty()) {
      this.errorMessage.set("You can not order a Non-Warranty Part on a Warranty Order")} else if ((this.entityLineHeaders() && this.entityLineHeaders().length > 0)
      && (!this.warrantyLines() || this.warrantyLines().length == 0)
      && this.warranty()) {
      this.errorMessage.set("You can not order a Warranty Part on a Non-Warranty Order")} else {
      this.isBusy.set(true);
      this.errorMessage.set(null!);
      this.searchResult.set(null!);
      let entityId: string = this.isAddQuote() ? '' : this.entity.id.entityId;
      let entityRevision: number = this.isAddQuote() ? 0 : this.entity.id.entityRevision;
      let searchInputCodeValue = this.inputControlCodeValue(this.partSearchTermInputControl.value);
      this.serialNumberResearchService.getPartSerialInformation(searchInputCodeValue).subscribe({
        next: result => {
          if (result) {
            this.partSerialInformation.set(result);
            this.doSerialNumberLookups(entityId, entityRevision);
          } else {
            this.doPartNumberLookups(entityId, entityRevision);
          }
        }, error: error => this.handleError(error),
      });
    }
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  public isAddQuote(): boolean {
    return this.entity == undefined || this.entity == null;
  }

  public noPartsSelected(): boolean {
    if (!this.searchResult()?.parts?.length) {
      return true;
    } else {
      return this.searchResult().parts.filter(part => part.quantity > 0).length === 0;
    }
  }

  public openHelpDialog(): void {
    this.dialogService.showPartsHelpDialog();
  }

  public openIndividualPartsDialog(part: Part) {
    this.dialogService.showIndividualPartDialog(part);
  }

  cannotCreateQuote(): boolean {
    if (this.noPartsSelected()) {
      return true;
    }
    if (this.warranty()) {
      return !this.passesWarrantyFieldsTests();
    }
    return false;
  }

  public passesWarrantyFieldsTests(): boolean {
    const installDateTest: boolean = this.searchResult()?.installDate?.getFullYear() > 0;
    const notesTest: boolean = this.warrantyRequestNotes?.trim().length > 0 && this.warrantyRequestNotes.length <= this.maxWarrantyTextLength;
    const milesTest: boolean = this.searchResult()?.miles?.length > 0;
    const defectTest: boolean = this.searchResult()?.defectCodeId > 0;
    const failurDateTest: boolean = this.searchResult()?.failureDate?.getFullYear() > 0;
    const dealerAddressTest: boolean = this.dealerAddress()?.addressId?.length > 0;
    return notesTest && milesTest && defectTest && failurDateTest && installDateTest && dealerAddressTest;
  }

  private loadEntityLineHeaders(): void {
    this.entityLineService.getEntityLines(this.entity.id).subscribe({
      next: results => {
        this.entityLineHeaders.set(results);
      },
      error: error => this.handleError(error)
    });
  }

  private loadWarrantyLines(): void {
    this.warrantyLineService.getWarrantyLines(this.entity.id.entityId, this.entity.id.entityRevision).subscribe(
      {
        next: results => {
          this.warrantyLines.set(results);
        },
        error: error => this.handleError(error)
      }
    );
  }

  protected readonly ProjectProductType = ProjectProductType;

  public selectAddress(): void {
    if (this.addressSelectionDisabled()) {
      return;
    }
    let addressDialog = this.dialog.open(DealerAddressSelectionDialogComponent, {
      data: {
        entity: JSON.parse(JSON.stringify(this.searchResult())),
        personLogin: this.personLogin,
      },
    });
    addressDialog.afterClosed().subscribe(result => {
      if (result) {
        this.dealerAddress.set(result);
      }
    });
  }

  getDealerAddress(): void {
    if (this.entity?.dealerAddressId?.length > 0) {
      this.addressService.getAddress(this.entity.dealerAddressId).subscribe({
        next: (address: OrganizationAddress) => {
              this.isBusy.set(true);
          if (address) {
            this.dealerAddress.set(address);
          }
        },
        error: error => this.handleError(error),
        complete: () => {
          this.isBusy.set(false);
        }
      });
    }
  }

  addressSelectionDisabled(): boolean {
    return this.entity?.dealerAddressId?.length > 0 && this.dealerAddress()?.addressId?.length > 0;
  }

  getDealerDescription(): string {
      return this.dealerAddress() ? this.dealerAddress()?.attentionLine + '; ' + this.dealerAddress()?.addressLine1 + ' ' +
        this.dealerAddress()?.cityName + ', ' + this.dealerAddress()?.stateCode + ' ' + this.dealerAddress()?.postalCode : null!;
  }
}
